/**
 * SpeechStateMachine - Manages speech recognition state transitions
 * 
 * Handles state transitions, retry logic, and backoff for speech recognition
 */

import { SpeechStates } from './SpeechEngine.js';

export class SpeechStateMachine {
    constructor(options = {}) {
        this.currentState = SpeechStates.IDLE;
        this.previousState = null;
        this.retryCount = 0;
        this.maxRetries = options.maxRetries || 3;
        this.baseDelay = options.baseDelay || 1000;
        this.maxDelay = options.maxDelay || 10000;
        this.onStateChange = null;
        this.retryTimeout = null;
        this.stateHistory = [];
        
        // State transition rules
        this.transitions = {
            [SpeechStates.IDLE]: [SpeechStates.ACQUIRING_MIC, SpeechStates.ERROR],
            [SpeechStates.ACQUIRING_MIC]: [SpeechStates.LISTENING, SpeechStates.ERROR, SpeechStates.IDLE],
            [SpeechStates.LISTENING]: [SpeechStates.ENDPOINTED, SpeechStates.ERROR, SpeechStates.IDLE],
            [SpeechStates.ENDPOINTED]: [SpeechStates.LISTENING, SpeechStates.IDLE, SpeechStates.ERROR],
            [SpeechStates.ERROR]: [SpeechStates.IDLE, SpeechStates.ACQUIRING_MIC]
        };
    }

    /**
     * Transition to a new state
     * @param {string} newState - Target state
     * @param {Object} context - Additional context for the transition
     * @returns {boolean} Success status
     */
    transitionTo(newState, context = {}) {
        // Allow same-state transitions if they have any reason (prevents warnings during initialization/cleanup)
        if (this.currentState === newState && !context.reason) {
            console.warn(`Invalid transition from ${this.currentState} to ${newState} without reason`);
            return false;
        }
        
        if (!this.canTransitionTo(newState)) {
            console.warn(`Invalid transition from ${this.currentState} to ${newState}`);
            return false;
        }

        const oldState = this.currentState;
        this.previousState = oldState;
        this.currentState = newState;
        
        // Record state history (keep last 10 transitions)
        this.stateHistory.push({
            from: oldState,
            to: newState,
            timestamp: Date.now(),
            context
        });
        if (this.stateHistory.length > 10) {
            this.stateHistory.shift();
        }

        console.log(`🔄 Speech State: ${oldState} → ${newState}`, context);
        
        // Notify listeners
        if (this.onStateChange) {
            this.onStateChange({
                from: oldState,
                to: newState,
                current: newState,
                context
            });
        }

        return true;
    }

    /**
     * Check if transition to new state is valid
     * @param {string} newState - Target state
     * @returns {boolean} Valid transition
     */
    canTransitionTo(newState) {
        const validTransitions = this.transitions[this.currentState] || [];
        return validTransitions.includes(newState);
    }

    /**
     * Get current state
     * @returns {string} Current state
     */
    getCurrentState() {
        return this.currentState;
    }

    /**
     * Get previous state
     * @returns {string} Previous state
     */
    getPreviousState() {
        return this.previousState;
    }

    /**
     * Check if in a specific state
     * @param {string} state - State to check
     * @returns {boolean} Is in state
     */
    isInState(state) {
        return this.currentState === state;
    }

    /**
     * Check if currently listening
     * @returns {boolean} Is listening
     */
    isListening() {
        return this.currentState === SpeechStates.LISTENING;
    }

    /**
     * Check if in error state
     * @returns {boolean} Is in error state
     */
    isInError() {
        return this.currentState === SpeechStates.ERROR;
    }

    /**
     * Handle error with retry logic
     * @param {Error} error - Error object
     * @param {Function} retryCallback - Function to call on retry
     * @returns {boolean} Will retry
     */
    handleError(error, retryCallback) {
        this.retryCount++;
        
        if (this.retryCount <= this.maxRetries) {
            const delay = this.calculateBackoffDelay();
            console.log(`🔄 Retrying speech recognition in ${delay}ms (attempt ${this.retryCount}/${this.maxRetries})`);
            
            this.retryTimeout = setTimeout(() => {
                this.retryTimeout = null;
                if (retryCallback) {
                    retryCallback();
                }
            }, delay);
            
            return true;
        } else {
            console.error(`❌ Max retries exceeded for speech recognition:`, error);
            this.transitionTo(SpeechStates.ERROR, { error, retryCount: this.retryCount });
            return false;
        }
    }

    /**
     * Calculate exponential backoff delay
     * @returns {number} Delay in milliseconds
     */
    calculateBackoffDelay() {
        const delay = Math.min(
            this.baseDelay * Math.pow(2, this.retryCount - 1),
            this.maxDelay
        );
        // Add jitter to prevent thundering herd
        return delay + Math.random() * 1000;
    }

    /**
     * Reset retry count
     */
    resetRetryCount() {
        this.retryCount = 0;
    }

    /**
     * Clear retry timeout
     */
    clearRetryTimeout() {
        if (this.retryTimeout) {
            clearTimeout(this.retryTimeout);
            this.retryTimeout = null;
        }
    }

    /**
     * Set state change callback
     * @param {Function} callback - Callback function
     */
    setOnStateChange(callback) {
        this.onStateChange = callback;
    }

    /**
     * Get state history
     * @returns {Array} State transition history
     */
    getStateHistory() {
        return [...this.stateHistory];
    }

    /**
     * Reset to idle state
     */
    reset() {
        this.clearRetryTimeout();
        this.retryCount = 0;
        this.transitionTo(SpeechStates.IDLE, { reason: 'reset' });
    }

    /**
     * Cleanup resources
     */
    destroy() {
        this.clearRetryTimeout();
        this.onStateChange = null;
        this.stateHistory = [];
    }
}
